import { Injectable, inject } from '@angular/core';

import { firstValueFrom, lastValueFrom } from 'rxjs';

import { TellentHttpApiService } from '@core/tellent-http-legacy';
import {
  dispatchAuthReady,
  injectIsNativeMobileHr,
  MOBILE_PUSH_EVENT,
  MobileDeviceDescriptor,
  MobilePushTokenReady,
} from '@core/tellent-mobile-contracts';

const STORAGE_KEY = 'tellent-hr-mobile.deviceGuid';

interface DeviceResponse {
  data: { guid: string };
}

/**
 * Bridges Capacitor-driven push events from `tellent-mobile-shell-logic` to
 * the Tellent Suite REST API.
 *
 * All cross-boundary communication  goes through window CustomEvents whose
 * names and payload shapes are declared in `@core/tellent-mobile-contracts`.
 *
 * Lifecycle:
 *  - On construction (after auth) we dispatch `authReady` to ask the shell
 *    for the device descriptor.
 *  - The shell answers with `registerDevice` carrying the descriptor and —
 *    if push permission was already granted — the OS-issued token. We POST
 *    once to upsert the device with credentials and remember the returned
 *    guid.
 *  - PATCH is reserved for the case where the user opts into push from the
 *    in-app settings (or for later token rotations) — `pushTokenReady` →
 *    PATCH against the stored guid.
 *  - On `beforeSignOut` we push a DELETE promise into the cleanup array so
 *    the device is unregistered while auth is still valid.
 */
@Injectable({ providedIn: 'root' })
export class MobilePushDeviceRegistrationService {
  private readonly http = inject(TellentHttpApiService);
  private readonly isNativeMobile = injectIsNativeMobileHr();

  private deviceGuid: string | null = window.localStorage.getItem(STORAGE_KEY);

  constructor() {
    if (!this.isNativeMobile) return;
    this.attachListeners();
    dispatchAuthReady();
  }

  private attachListeners(): void {
    window.addEventListener(MOBILE_PUSH_EVENT.registerDevice, event => {
      const detail = (<CustomEvent<MobileDeviceDescriptor>>event).detail;
      void this.registerDevice(detail);
    });

    window.addEventListener(MOBILE_PUSH_EVENT.pushTokenReady, event => {
      const detail = (<CustomEvent<MobilePushTokenReady>>event).detail;
      void this.updatePushToken(detail.token);
    });

    window.addEventListener(MOBILE_PUSH_EVENT.beforeSignOut, event => {
      const detail = (<CustomEvent<{ cleanupPromises: Array<Promise<unknown>> }>>event).detail;
      detail.cleanupPromises.push(this.unregisterDevice());
    });
  }

  private async registerDevice(descriptor: MobileDeviceDescriptor): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.post<DeviceResponse>('/notifications/devices', null, descriptor, {
          skipCompany: true,
        }),
      );
      this.deviceGuid = response.data.guid;
      window.localStorage.setItem(STORAGE_KEY, this.deviceGuid);
    } catch (e) {
      console.error('[push] device registration failed', e);
    }
  }

  private async updatePushToken(token: string): Promise<void> {
    if (!this.deviceGuid) return;
    try {
      await firstValueFrom(
        this.http.patch(
          `/notifications/devices/${this.deviceGuid}`,
          null,
          { token },
          { skipCompany: true },
        ),
      );
    } catch (e) {
      console.error('[push] device token update failed', e);
    }
  }

  private async unregisterDevice(): Promise<void> {
    const guid = this.deviceGuid;
    if (!guid) return;
    try {
      await lastValueFrom(
        this.http.delete(`/notifications/devices/${guid}`, null, { skipCompany: true }),
      );
      this.deviceGuid = null;
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.error('[push] device unregistration failed', e);
      window.dispatchEvent(
        new CustomEvent(MOBILE_PUSH_EVENT.unregisterError, { detail: { error: e } }),
      );
    }
  }
}
